import { useEffect, useRef, useState, type JSX } from 'react';
import { BootSplash } from './BootSplash';

/**
 * Cold-boot loading chrome over the Cesium globe (#259). Covers the globe
 * viewport from first mount — when MapView's `<div>` exists but Cesium's canvas
 * is still a black, imagery-less frame — and fades out only on REAL readiness.
 *
 * Truth-in-paint: dismissal is driven by `ready` (wired in MapView off the
 * imagery-tile-queue drain — see cesiumImageryReadiness), NOT a timer, so the
 * overlay never reveals a still-black globe.
 *
 * Fallback cap: a slow / failed imagery stream (bad Ion token, offline) must
 * never trap the operator behind the overlay — after GLOBE_BOOT_CAP_MS the
 * overlay fades regardless (the globe is at least interactive by then).
 *
 * Renders the shared BootSplash so the globe stage matches the auth-probe stage
 * exactly — one continuous boot.
 */

/**
 * Hard fallback cap (ms). The overlay fades after this even if imagery never
 * reports ready, so a degraded imagery stream can't trap the operator. This is a
 * founder-specified ceiling (#259, "~12s") — changing it is a deliberate product
 * decision; the pinned test in GlobeBootOverlay.test.tsx surfaces an edit as
 * sign-off.
 */
export const GLOBE_BOOT_CAP_MS = 12_000;

/** Opacity-fade duration (ms). Kept in lockstep with the `duration-500` class. */
export const GLOBE_BOOT_FADE_MS = 500;

type Phase = 'shown' | 'fading' | 'gone';

export function GlobeBootOverlay({
  ready,
  capMs = GLOBE_BOOT_CAP_MS,
  line,
  onDismissed,
}: {
  /** True once base imagery is genuinely loaded for the current view. */
  ready: boolean;
  /** Fallback cap before forcing the fade; defaults to GLOBE_BOOT_CAP_MS. */
  capMs?: number;
  /** Optional status line override (defaults to the BootSplash copy). */
  line?: string;
  /** Fired once, after the overlay has fully faded out and unmounted. */
  onDismissed?: () => void;
}): JSX.Element | null {
  const [phase, setPhase] = useState<Phase>('shown');
  const onDismissedRef = useRef(onDismissed);
  onDismissedRef.current = onDismissed;

  // Primary, event-driven dismissal: imagery is genuinely ready.
  useEffect(() => {
    if (ready) setPhase((p) => (p === 'shown' ? 'fading' : p));
  }, [ready]);

  // Fallback cap: force the fade if readiness never arrives.
  useEffect(() => {
    if (phase !== 'shown') return;
    const t = setTimeout(() => setPhase('fading'), capMs);
    return () => clearTimeout(t);
  }, [phase, capMs]);

  // Once fading, unmount after the fade completes. Timer-driven (not a CSS
  // transitionend) so it's deterministic in tests and under happy-dom.
  useEffect(() => {
    if (phase !== 'fading') return;
    const t = setTimeout(() => {
      setPhase('gone');
      onDismissedRef.current?.();
    }, GLOBE_BOOT_FADE_MS);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === 'gone') return null;

  return (
    <div
      data-testid="globe-boot-overlay"
      aria-hidden={phase === 'fading'}
      className={`pointer-events-none absolute inset-0 z-20 transition-opacity duration-500 ${
        phase === 'fading' ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <BootSplash testId="globe-boot-splash" line={line} />
    </div>
  );
}
